import {
  isMediaCardString,
  keyToMediaCardType,
  type MediaCardString,
  type MediaCardType,
} from '@wandb/weave/common/types/media';

import {useMemoizedSelector} from '../../../../state/hooks';
import {useActiveHistoryKeyInfo} from '../../../../state/panelGenHistoryKeys/useActiveHistoryKeyInfo';
import {
  exactKeysPattern,
  keysWithinLookupLimit,
} from '../../../../state/panelGenHistoryKeys/usePanelVisibilityKeysSync';
import {useRampFlagMediaSettingsDrawerAllTypesUser} from '../../../../util/rampFeatureFlags/rampFeatureFlags';
import {useMetricKeySearch} from '../../../MetricSearch/useMetricKeySearch';
import {getMediaKeys} from '../../../PanelMediaBrowser/utils/getMediaKeys';
import {useMaybePanelsSharedRefsContext} from '../../../Views/contexts/PanelsSharedRefsContext';
import {useMaybeWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {convertMetricSearchResultsToKeyInfo} from '../../PanelsQuickAdd/hooks/useSearchedKeys';
import {useMediaKeysAndCardTypes, useSectionMediaKeys} from '../hooks';
import {selectAllMediaPanelConfigsWithRefs} from './mediaSettingsSelectors';

/**
 * Card types for `mediaKeys`. NGW samples history keys, so with
 * `media_settings_drawer_all_types_user` on, keys the sample missed are looked
 * up by exact name. `mediaCardTypes` is undefined while that lookup loads.
 */
export function useMediaCardTypesWithLookup(mediaKeys: string[]): {
  mediaCardTypes: MediaCardType[] | undefined;
  loading: boolean;
} {
  const {historyKeyInfo} = useActiveHistoryKeyInfo();
  const sharedRefs = useMaybePanelsSharedRefsContext();
  const isAllTypesEnabled =
    useRampFlagMediaSettingsDrawerAllTypesUser() === true;

  const {keyTypes} = getMediaKeys(historyKeyInfo);
  const missingKeys =
    isAllTypesEnabled && sharedRefs != null
      ? keysWithinLookupLimit(mediaKeys.filter(key => keyTypes[key] == null))
      : [];
  const skip = missingKeys.length === 0;

  const {results, loading} = useMetricKeySearch(
    sharedRefs?.runSetRefs ?? [],
    sharedRefs?.customRunNamesRef,
    {
      metricNamePattern: skip ? undefined : exactKeysPattern(missingKeys),
      pageSize: Math.max(missingKeys.length, 1),
      skip,
    }
  );

  if (!skip && loading) {
    return {mediaCardTypes: undefined, loading: true};
  }

  const lookedUpKeyInfo = convertMetricSearchResultsToKeyInfo(results);
  const mediaCardTypes = mediaKeys
    .map(key => keyTypes[key] ?? lookedUpKeyInfo[key]?.typeCounts?.[0]?.type)
    .filter(
      (mediaString): mediaString is MediaCardString =>
        mediaString != null && isMediaCardString(mediaString)
    )
    .map(keyToMediaCardType);
  return {mediaCardTypes, loading: false};
}

/**
 * Workspace media keys and their card types for the media settings drawer.
 * With `media_settings_drawer_all_types_user` on, this adds the keys media
 * panels are configured with, and looks up types the history-key sample missed.
 */
export function useWorkspaceMediaKeysAndCardTypesWithLookup(): {
  mediaKeys: string[];
  mediaCardTypes: MediaCardType[] | undefined;
  loading: boolean;
} {
  const {mediaKeys: sampledMediaKeys} = useMediaKeysAndCardTypes();
  const isAllTypesEnabled =
    useRampFlagMediaSettingsDrawerAllTypesUser() === true;
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const mediaPanelConfigs = useMemoizedSelector(
    selectAllMediaPanelConfigsWithRefs,
    workspaceRefs?.panelBankConfigRef
  );
  const mediaKeys = isAllTypesEnabled
    ? Array.from(
        new Set([
          ...sampledMediaKeys,
          ...mediaPanelConfigs.flatMap(({config}) => config.mediaKeys ?? []),
        ])
      )
    : sampledMediaKeys;
  const {mediaCardTypes, loading} = useMediaCardTypesWithLookup(mediaKeys);
  return {mediaKeys, mediaCardTypes, loading};
}

export function useSectionMediaKeysAndCardTypes(): {
  mediaKeys: string[];
  mediaCardTypes: MediaCardType[] | undefined;
  loading: boolean;
} {
  const mediaKeys = useSectionMediaKeys();
  const {mediaCardTypes, loading} = useMediaCardTypesWithLookup(mediaKeys);
  return {mediaKeys, mediaCardTypes, loading};
}
